import type { AgentState, Member } from "@bundle/contracts";
import { Access, Live, Shell, type MessageKey } from "@bundle/core";
import {
  AgentBadge,
  Avatar,
  Button,
  CreateAgentSheet,
  Divider,
  Icon,
  ScrollView,
  Stack,
  Text,
  useNavigation,
  useT,
  useWorkspace,
  type ChannelOption,
} from "@bundle/ui";
import { useState } from "react";

const STATE_KEYS: Record<AgentState, MessageKey> = {
  idle: "agent.state.idle",
  working: "agent.state.working",
  waiting: "agent.state.waiting",
  error: "agent.state.error",
  offline: "agent.state.offline",
};

function AgentCard({ agent, sponsor, onMessage }: { agent: Member; sponsor?: string; onMessage: () => void }) {
  const t = useT();
  const ws = useWorkspace();
  const live = Live.agentState(ws.state.live, agent);
  const busy = live.state === "working" || live.state === "waiting";
  return (
    <Stack direction="horizontal" align="start" gap="4" padding="4" radius="lg" border="border.subtle" background="bg.canvas" testID={`agent-card-${agent.userId}`}>
      <Avatar name={agent.displayName} src={agent.avatarUrl} size="avatar.lg" square presence={live.state === "offline" ? "away" : "online"} />
      <Stack grow gap="1.5">
        <Stack direction="horizontal" align="center" gap="2">
          <Text variant="headline" weight="semibold">
            {agent.displayName}
          </Text>
          <AgentBadge />
          <Text variant="caption" color="text.tertiary">
            {agent.agent?.runtime === "external" ? t("agent.runtime.external") : t("agent.runtime.hosted")}
          </Text>
        </Stack>
        {agent.agent?.description === undefined ? null : (
          <Text color="text.secondary" lines={2}>
            {agent.agent.description}
          </Text>
        )}
        <Stack direction="horizontal" align="center" gap="1.5">
          <Icon name={busy ? "sparkles" : live.state === "error" ? "alert" : "clock"} size="icon.sm" color={busy ? "agent.fg" : "text.tertiary"} />
          <Text variant="footnote" weight="medium" color={busy ? "agent.fg" : "text.tertiary"} lines={1}>
            {live.activity ?? t(STATE_KEYS[live.state])}
          </Text>
        </Stack>
        {sponsor === undefined ? null : (
          <Text variant="caption" color="text.tertiary">
            {t("agent.sponsor", { name: sponsor })}
          </Text>
        )}
      </Stack>
      <Button label={t("agent.message")} icon="message" size="sm" onPress={onMessage} testID={`agent-message-${agent.userId}`} />
    </Stack>
  );
}

/** Directory of the workspace's agents with live status; sponsors can create new ones. */
export function AgentsScreen() {
  const t = useT();
  const nav = useNavigation();
  const ws = useWorkspace();
  const [creating, setCreating] = useState(false);
  const cache = ws.state.cache;
  if (cache === undefined) return <Stack grow />;
  const me = cache.members[ws.state.meId];
  const now = ws.vm.deps.clock.now();
  const canCreate = me !== undefined && Access.canSponsor(me, now);
  const agents = Object.values(cache.members)
    .filter((m) => m.kind === "agent" && !m.deactivated)
    .toSorted((a, b) => (a.displayName.toLowerCase() < b.displayName.toLowerCase() ? -1 : 1));
  const channels: ChannelOption[] = Object.values(cache.channels)
    .filter((c) => (c.kind === "public" || c.kind === "private") && cache.memberships[c.id] !== undefined)
    .map((c) => ({ id: c.id, name: c.name }));
  return (
    <Stack grow testID="agents-screen">
      <Stack direction="horizontal" align="center" gap="3" paddingX="8" paddingY="5">
        <Stack grow gap="1">
          <Text variant="title2" weight="bold">
            {t("agents.title")}
          </Text>
          <Text color="text.secondary">{t("agents.subtitle")}</Text>
        </Stack>
        {canCreate ? <Button label={t("agent.create.title")} icon="plus" variant="primary" onPress={() => setCreating(true)} testID="agents-create" /> : null}
      </Stack>
      <Divider />
      <ScrollView padding="8" maxContentWidth="content.readable">
        <Stack gap="3">
          {agents.length === 0 ? <Text color="text.tertiary">{t("agents.empty")}</Text> : null}
          {agents.map((a) => {
            const sponsorId = a.access?.sponsorId;
            const sponsor = sponsorId === undefined ? undefined : cache.members[sponsorId]?.displayName;
            return (
              <AgentCard key={a.userId} agent={a} sponsor={sponsor} onMessage={() => nav.open(Shell.formatUrl({ type: "dm", ws: ws.slug, userId: a.userId }))} />
            );
          })}
        </Stack>
      </ScrollView>
      <CreateAgentSheet
        open={creating}
        onClose={() => setCreating(false)}
        channels={channels}
        onSubmit={async (req) => (await ws.vm.createAgent(req)).token}
      />
    </Stack>
  );
}
